import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { getScopeFilter, setScopeFilter } from '../shared/helpers/scopefilterHelpers.ts';
import { getScopes, getScopesTypeRegex, type ScopeRegEx, type ScopeWithDescription } from '@/shared/helpers/api.ts';
import { http } from '../shared/httpWrapper.js';
import _ from 'lodash';

export const useScopeFilterStore = defineStore('scopeFilter', () => {
    const scopes = ref<ScopeWithDescription[]>([]);
    const selectedScopeFilter = ref<string[]>([]);
    const scopeFilterLoading = ref(true);
    const scopeType = ref<string | null>(null);

    const lastUpdated = ref<Date | null>(null);
    const scopeFilterRegex = ref<RegExp | null>(null);

    const filterIcon = computed(() => {
        return selectedScopeFilter.value.length > 0 ? 'filter' : 'filter-off';
    });

    function updateScopes(scps: ScopeWithDescription[]) {
        lastUpdated.value = new Date();
        // Consumers render identity and description, so the API objects must be kept intact.
        scopes.value = scps.toSorted((a, b) => a.identity.localeCompare(b.identity));
    }

    function setScopeType(type: string | null) {
        if (scopeType.value !== type) {
            scopeType.value = type;
            lastUpdated.value = null;
            scopeFilterRegex.value = null;
            selectedScopeFilter.value = [];
        }
    }

    function updateScopeFilter(scope: string | string[], saveToLocalStorage = true) {
        let scopeArray;
        if (typeof scope === 'string') {
            scopeArray = [scope];
        } else {
            scopeArray = scope;
        }

        const regex = scopeFilterRegex.value;
        if (regex !== null) {
            scopeArray = scopeArray.filter(scp => regex.test(scp));
        }

        scopeArray.forEach(scp => {
            if (selectedScopeFilter.value.includes(scp)) {
                selectedScopeFilter.value = selectedScopeFilter.value.filter(s => s !== scp);
            } else {
                selectedScopeFilter.value = selectedScopeFilter.value.concat([scp]).toSorted();
            }
        });
        if (saveToLocalStorage) {
            setScopeFilter(scopeType.value, selectedScopeFilter.value);
        }
    }

    function setRegEx(regEx: string | null) {
        if (regEx !== null) {
            scopeFilterRegex.value = new RegExp(regEx);
        }
    }

    async function init() {
        scopeFilterLoading.value = true;

        const timediff = lastUpdated.value ? (Date.now() - lastUpdated.value.getTime()) / 1000 : Infinity;
        const cacheAgeInSeconds = 30;

        if (timediff > cacheAgeInSeconds) {
            const response = await getScopes(http, scopeType.value ?? '');

            updateScopes(response);
            const scopeTypeRegExRes = await getScopesTypeRegex(http, scopeType.value ?? '');

            if (scopeTypeRegExRes !== '') {
                const scopeTypeRegEx = scopeTypeRegExRes as ScopeRegEx;
                setRegEx(scopeTypeRegEx.regEx);
            }
            const localStorageScopeFilter = getScopeFilter(scopeType.value);
            const combined = _.concat(selectedScopeFilter.value, localStorageScopeFilter);
            if (localStorageScopeFilter.length > 0) {
                updateScopeFilter(combined, false);
            }
        }

        scopeFilterLoading.value = false;
    }

    function clearScopeFilter() {
        selectedScopeFilter.value = [];
        setScopeFilter(scopeType.value, selectedScopeFilter.value);
    }

    watch(selectedScopeFilter, () => {
        if (!scopeFilterLoading.value) {
            const localStorageValue = getScopeFilter(scopeType.value);
            if (!_.isEqual(selectedScopeFilter.value, localStorageValue)) {
                setScopeFilter(scopeType.value, selectedScopeFilter.value);
            }
        }
    });

    return {
        scopes,
        scopeType,
        selectedScopeFilter,
        scopeFilterLoading,
        filterIcon,
        lastUpdated,
        scopeFilterRegex,
        setScopeType,
        setRegEx,
        updateScopes,
        updateScopeFilter,
        clearScopeFilter,
        init
    };
});
